/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout, and the picker idiom this screen needs).
 *
 * Only the data, columns and labels are ours. Reuses the same sources brand's own New.tsx cites
 * (screen-map.md row 11).
 */
import { useState } from "react";
import { useLoaderData, useNavigate, useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiTagDuotone as ClassificationIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { imageUrl, useFileUpload } from "@steelyard/common";
import { createProduct, listBrands, listCategoryTree, type Brand, type Category } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { ActionIcon, Textarea } from "rizzui";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";
import { productPath } from "../../../lib/paths";

/**
 * Active brands and categories only (project owner, 2026-09-29). The backend still accepts an
 * inactive one — this is what the form offers, not a rule the client enforces.
 */
export async function productNewLoader() {
  const [brands, categories] = await Promise.all([listBrands(1, true, 200), listCategoryTree(true)]);
  if (!brands || !categories) throw new Error("The API returned no body for the brand/category pickers.");
  return { brands, categories };
}

const productSchema = z.object({
  brandId: z.string().trim().min(1, "Required"),
  categoryId: z.string().trim().min(1, "Required"),
  name: z.string().trim().min(1, "Required").max(200),
  description: z.string().trim().max(2000).optional().or(z.literal("")),
});
type ProductFormValues = z.infer<typeof productSchema>;

/** Ported from the stale apps/admin/app/catalogue/products/new/page.tsx path in tasks.md T046. */
export default function NewProduct() {
  const navigate = useNavigate();
  const goBack = useGoBack("/catalogue/products");
  const { brands, categories } = useLoaderData() as Awaited<ReturnType<typeof productNewLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [images, setImages] = useState<string[]>([]);
  const gallery = useFileUpload("image", {
    multiple: true,
    onUploaded: (uploaded, files) => {
      setImages((prev) => [...prev, ...uploaded.map((result) => result.hash)]);
      toast.success(files.length > 1 ? "Images uploaded" : "Image uploaded");
    },
    onError: () => toast.error("Could not upload the image(s)"),
  });

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="create a product" />;
  }

  function removeImage(hash: string) {
    setImages((prev) => prev.filter((h) => h !== hash));
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="New Product"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue · Products", href: "/catalogue/products" }, { name: "New Product" }]}
      />

      <Form<ProductFormValues>
        validationSchema={productSchema}
        useFormProps={{ defaultValues: { brandId: "", categoryId: "", name: "", description: "" } }}
        className={formCard}
        onSubmit={async (values) => {
          try {
            const { id } = await createProduct({
              brandId: values.brandId,
              categoryId: values.categoryId,
              name: values.name,
              description: values.description || null,
              images: images.length > 0 ? images : null,
            });
            toast.success("Product created");
            // Unlike brand's and category's own New.tsx, this one redirects to the new product's
            // own page rather than the list: a product's packing-screen image (Detail.tsx's "Packing
            // image" section) can only be uploaded once the product has an id, so landing there is
            // what lets a person finish that step right away instead of hunting the row back down.
            navigate(productPath(id), { replace: true }); // the id URL redirects to the slug one
          } catch (err) {
            toast.error(problemTitle(err, "Could not create the product"));
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={ClassificationIcon}>Classification</SectionTitle>} description="Brand and category decide which discount rules can match this product's SKUs.">
                <SelectField
                  label="Brand"
                  required
                  disabled={brands.items.length === 0}
                  disabledReason={brands.items.length === 0 ? "No brands available." : undefined}
                  error={errors.brandId?.message}
                  {...register("brandId")}
                >
                  <option value="">{brands.items.length === 0 ? "No brands available" : "Choose a brand…"}</option>
                  {brands.items.map((b: Brand) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  label="Category"
                  required
                  disabled={categories.items.length === 0}
                  disabledReason={categories.items.length === 0 ? "No categories available." : undefined}
                  error={errors.categoryId?.message}
                  {...register("categoryId")}
                >
                  <option value="">{categories.items.length === 0 ? "No categories available" : "Choose a category…"}</option>
                  {categories.items.map((c: Category) => (
                    <option key={c.id} value={c.id}>
                      {"  ".repeat(c.depth)}
                      {c.name}
                    </option>
                  ))}
                </SelectField>
              </FormGroup>
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="What customers see on the product page.">
                <Field label="Name" required maxLength={200} autoComplete="off" wrapperClassName="col-span-full" error={errors.name?.message} {...register("name")} />
                <Textarea
                  label="Description"
                  rows={3}
                  className="col-span-full"
                  helperText="Optional."
                  error={errors.description?.message}
                  {...register("description")}
                />
              </FormGroup>
              <FormGroup title={<SectionTitle icon={ImageIcon}>Gallery images</SectionTitle>} description="Optional. Uploaded as real files, not linked. The first one is the primary image.">
                <div className="col-span-full flex flex-col gap-4">
                  <Upload
                    accept="img"
                    multiple
                    disabled={gallery.busy}
                    onChange={gallery.onChange}
                    aria-label="Product gallery image files"
                    placeholderText={gallery.busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                    iconClassName="size-8 shrink-0"
                    className="p-4"
                  />
                  {images.length > 0 ? (
                    <div className="flex flex-wrap gap-3">
                      {images.map((hash) => (
                        <div key={hash} className="relative">
                          <img src={imageUrl(hash)} alt="Uploaded product image" className="h-20 w-20 rounded-lg border border-muted object-cover" />
                          <ActionIcon
                            type="button"
                            size="sm"
                            variant="solid"
                            color="danger"
                            className="absolute -right-2 -top-2"
                            aria-label="Remove this image"
                            onClick={() => removeImage(hash)}
                          >
                            <RemoveIcon aria-hidden className="size-3.5" />
                          </ActionIcon>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
                {gallery.error ? <ErrorSurface error={gallery.error} action="upload these images" /> : null}
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Creating…" : "Create product"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
